import { generateSocialShareUrl, SocialPlatform } from "@/utils/socialMediaUtils.ts";
import { DocumentRef } from "@/api";

/**
 * Generates a prefilled post URL for the specified platform
 * @param postData - The text content to share
 * @param platform - The social media platform to generate the URL for
 * @returns The generated sharing URL
 */
export function generatePrefilledPostUrl(postData: string | undefined, platform: SocialPlatform = "linkedin"): string {
  return generateSocialShareUrl(platform, postData);
}

/**
 * Triggers a download of the specified document
 * @param fileName - The name of the file to download
 * @param toDownload - The document to download
 */
export function triggerDownload(fileName: string, toDownload: DocumentRef) {
  const url = toDownload.url;
  if (!url) {
    return;
  }

  if (fileName.toLowerCase().endsWith(".md")) {
    // Ensure's that the browser downloads instead of displaying.
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      });
    return;
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
}

/**
 * Creates a documentation link by using the current URL as base,
 * removing query parameters and appending "/document.html"
 * @param userNickname - The nickname of the user (currently unused)
 * @returns The documentation URL string
 */
export function createDocumentationLinks(
  userNickname: string | undefined,
): { displayUrl: string; hrefUrl: string } | undefined {
  if (!userNickname) {
    return undefined;
  }
  const pdfPreviewerPrefix = `https://docs.google.com/viewer?url=`;
  const documentationUrl = `https://${window.location.hostname}/${userNickname}${window.location.pathname}/document.pdf`;
  const hrefUrl = `${pdfPreviewerPrefix}${documentationUrl}`;

  return {
    displayUrl: documentationUrl,
    hrefUrl,
  };
}

/**
 * Creates a portfolio documentation link by using the current URL as base,
 * @param documentId - The ID of the document
 * @returns The documentation URL string
 */
export function createPortfolioDocumentationLink(documentId: string): string {
  return `${window.location.origin}/portfolio/documents/${documentId}`;
}
